import {useContext} from 'react'

import Context from '@s-ui/react-context'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {STEPS} from './config.js'

const useLandingInstantOfferTracking = () => {
  const {page: pageTrack, track} = useAnalyticsTrack()

  const {domain} = useContext(Context)
  const config = domain.get('config')

  const {fieldNameToTrackingIds, formErrorTypes} = config.get('form')

  const getErrorFields = ({errors}) => {
    const errorFields = []
    for (const fieldId in errors) {
      if (errors[fieldId]?.length) errorFields.push(fieldNameToTrackingIds[fieldId].property)
    }
    return errorFields.join(':')
  }

  const getPageName = () => {
    const {LANDING_INSTANT_OFFER_TEST_PATTERN} = config.get('page')
    return domain.get('get_page_name_page_use_case').execute({path: LANDING_INSTANT_OFFER_TEST_PATTERN})
  }

  const getProperties = fields => {
    const properties = []
    for (const fieldId in fields) {
      if (fieldNameToTrackingIds[fieldId] && fields[fieldId]) properties.push(fieldNameToTrackingIds[fieldId].property)
    }
    return properties.join(':')
  }

  const pageTrackLandingStep = async () => {
    const {pageName} = await getPageName()

    const trackingData = await domain.get('get_page_tracking_data_use_case').execute({
      page: pageName,
      step: STEPS.LANDING
    })

    pageTrack(trackingData)
  }

  const pageTrackUserFormStep = async ({origin}) => {
    const {pageName} = await getPageName()

    const trackingData = await domain.get('get_page_tracking_data_use_case').execute({
      origin,
      page: pageName,
      step: STEPS.USER_FORM
    })

    pageTrack(trackingData)
  }

  const pageTrackVehicleSelectStep = async ({searchResultsCount}) => {
    const {pageName} = await getPageName()

    const trackingData = await domain.get('get_page_tracking_data_use_case').execute({
      page: pageName,
      searchResultsCount,
      step: STEPS.VEHICLE_SELECT
    })

    pageTrack(trackingData)
  }

  const pageTrackVehicleFormStep = async ({origin}) => {
    const {pageName} = await getPageName()

    const trackingData = await domain.get('get_page_tracking_data_use_case').execute({
      origin,
      page: pageName,
      step: STEPS.VEHICLE_FORM
    })

    pageTrack(trackingData)
  }

  const trackFaqExpanded = ({question}) => {
    const trackingData = {
      event: `Landing IO Faq Expanded`,
      data: {
        hit_information: question
      }
    }

    track(trackingData)
  }

  const trackFieldAutocompletion = ({fields = [], fieldId}) => {
    const fieldProperties = fieldId ? fieldNameToTrackingIds[fieldId]?.property : getProperties(fields)

    const trackingData = {
      event: `Landing IO Field Autocompleted`,
      data: {
        hit_information: fieldId ? `unica opcion` : `proceso matricula`,
        pta_automatic_field: fieldProperties
      }
    }

    track(trackingData)
  }

  const trackFieldChange = ({fieldId}) => {
    const trackingData = {
      event: `Landing IO ${fieldNameToTrackingIds[fieldId].field} Selected`
    }

    track(trackingData)
  }

  const trackSubmitPlateFormError = ({errorType = formErrorTypes.serverError, errors} = {}) => {
    const trackingData = {
      event: `Landing IO Car Plate Process Error`,
      data: {page_error: errorType}
    }

    if (errors) {
      trackingData.data.form_fields = getErrorFields({errors})
    }

    track(trackingData)
  }

  const trackSubmitPlateFormSuccess = () => {
    const trackingData = {
      event: `Landing IO Car Plate Process Success`
    }

    track(trackingData)
  }

  const trackSubmitUserFormError = ({errorType = formErrorTypes.serverError, errors} = {}) => {
    const trackingData = {
      event: `Landing IO Offer Requested Error`,
      data: {page_error: errorType}
    }

    if (errors) {
      trackingData.data.form_fields = getErrorFields({errors})
    }

    track(trackingData)
  }

  const trackSubmitUserFormSuccess = () => {
    const trackingData = {
      event: `Landing IO Offer Requested`
    }

    track(trackingData)
  }

  const trackSubmitVehicleFormError = ({errorType = formErrorTypes.serverError, errors} = {}) => {
    const trackingData = {
      event: `Landing IO Next Error`,
      data: {page_error: errorType}
    }

    if (errors) {
      trackingData.data.form_fields = getErrorFields({errors})
    }

    track(trackingData)
  }

  const trackSubmitVehicleFormSuccess = () => {
    const trackingData = {
      event: `Landing IO Next Confirmed`
    }

    track(trackingData)
  }

  const trackVideoPlayed = () => {
    const trackingData = {
      event: `Landing IO Video Played`
    }

    track(trackingData)
  }

  return {
    pageTrackLandingStep,
    pageTrackUserFormStep,
    pageTrackVehicleSelectStep,
    pageTrackVehicleFormStep,
    trackFaqExpanded,
    trackFieldAutocompletion,
    trackFieldChange,
    trackSubmitPlateFormError,
    trackSubmitPlateFormSuccess,
    trackSubmitUserFormError,
    trackSubmitUserFormSuccess,
    trackSubmitVehicleFormError,
    trackSubmitVehicleFormSuccess,
    trackVideoPlayed
  }
}

export {useLandingInstantOfferTracking}
